import { useEffect, useState, type ReactNode } from 'react';
import { Button } from 'components/ui/button';
import { nativeCall, type NativeCallState, type NativeListener } from './nativeBridge';

/** Hosted updates must not start a browser peer beside an older native call still alive in the app. */
export function NativeCallFallbackGuard({ children }: { children: ReactNode }) {
  const [state, setState] = useState<NativeCallState>();
  const [error, setError] = useState('');
  const [attempt, setAttempt] = useState(0);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    let mounted = true;
    let events = 0;
    let listener: NativeListener | undefined;
    const apply = (next: NativeCallState) => {
      if (!['idle', 'left', 'error', 'denied', 'preview', 'joining', 'joined', 'waiting'].includes(next?.state)) throw new Error('Unknown call state');
      if (mounted) { setState(next); setError(''); }
    };
    void nativeCall.listen(next => { events += 1; try { apply(next); } catch { if (mounted) setError('Unable to check the active call.'); } }).then(handle => {
      if (mounted) listener = handle; else void handle.remove();
    }).catch(() => { if (mounted) setError('Unable to check the active call.'); });
    const snapshot = events;
    void nativeCall.getState().then(next => { if (mounted && events === snapshot) apply(next); }).catch(() => { if (mounted) setError('Unable to check the active call.'); });
    return () => { mounted = false; void listener?.remove().catch(() => undefined); };
  }, [attempt]);
  if (!state || error) return <div role="status" className="flex h-full flex-col items-center justify-center gap-3 p-5 text-white">
    <p>{error || 'Checking for an active call…'}</p>
    {error && <Button onClick={() => { setError(''); setAttempt(value => value + 1); }}>Retry</Button>}
  </div>;
  if (['preview', 'joining', 'joined', 'waiting'].includes(state.state)) return <div className="flex h-full flex-col items-center justify-center gap-3 p-5 text-center text-white">
    <p>A call is already open in this app. Return to it or leave before starting a web call.</p>
    <Button onClick={() => void nativeCall.show().catch(() => setError('Unable to return to the active call.'))}>Return to active call</Button>
    <Button variant="secondary" disabled={busy} onClick={() => {
      setBusy(true);
      void nativeCall.leave(state).then(() => nativeCall.getState()).then(setState).catch(() => setError('Unable to leave the active call.')).finally(() => setBusy(false));
    }}>Leave active call</Button>
  </div>;
  return <>{children}</>;
}
